import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { ArrowLeft, Zap, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'

interface Credential {
  id: string | null
  carrier_code: string
  api_client_id: string | null
  api_client_secret: string | null
  api_client_id_masked: string | null
  api_client_secret_masked: string | null
  api_user_email: string | null
  active: boolean
  last_tested_at: string | null
  test_status: string | null  // "success", "failed", null
}

interface Props {
  carrier: { id: string; name: string; code: string | null }
  credential: Credential | null
}

export default function ApiCredentials({ carrier, credential }: Props) {
  const pathFor = useOrgPath()
  const [form, setForm] = useState({
    api_client_id: credential?.api_client_id || '',
    api_client_secret: credential?.api_client_secret || '',
    api_user_email: credential?.api_user_email || '',
  })
  const [testing, setTesting] = useState(false)

  const hasCredential = credential?.id != null
  const isConnected = credential?.active && credential?.test_status === 'success'

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const payload = { carrier_credential: { ...form, carrier_code: carrier.code } }

    router.patch(pathFor(`/settings/carriers/${carrier.id}/api_credentials`), payload, {
      onError: () => toast.error('Failed to save credentials'),
    })
  }

  const hasIdAndSecret = form.api_client_id.trim() !== '' && form.api_client_secret.trim() !== ''
  const canTest = hasIdAndSecret || (hasCredential && (form.api_client_id.trim() === '' && form.api_client_secret.trim() === ''))

  function handleTest() {
    setTesting(true)
    router.post(pathFor(`/settings/carriers/${carrier.id}/api_credentials/test`), {
      carrier_credential: form,
    }, {
      preserveState: true,
      onSuccess: () => {
        setTesting(false)
      },
      onError: () => {
        setTesting(false)
      },
    })
  }

  function handleRemove() {
    if (!confirm('Remove API credentials for this carrier? Live rate lookups will stop working.')) return
    router.delete(pathFor(`/settings/carriers/${carrier.id}/api_credentials`))
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <Link href={pathFor(`/settings/carriers/${carrier.id}`)} className="text-muted-foreground hover:text-foreground cursor-pointer">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-xl font-bold">{carrier.name} — API Integration</h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-8 space-y-8">
        {/* Status Row */}
        <div className="flex items-center gap-3">
          <span className="text-[13px] font-medium text-muted-foreground">Status:</span>
          {hasCredential ? (
            <Badge variant="secondary" size="lg" className={`font-medium rounded-full ${
              isConnected ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {isConnected ? 'Connected' : 'Credentials Set'}
            </Badge>
          ) : (
            <Badge variant="secondary" size="lg" className="font-medium rounded-full bg-gray-100 text-gray-500">
              Not Configured
            </Badge>
          )}
          {credential?.last_tested_at && (
            <span className="text-[12px] text-muted-foreground">
              Last tested: {new Date(credential.last_tested_at).toLocaleString()}
            </span>
          )}
        </div>

        {/* Form Section */}
        <form onSubmit={handleSave} className="max-w-[560px] space-y-5">
          <div>
            <h3 className="text-[15px] font-semibold text-foreground">API Credentials</h3>
            <p className="text-[13px] text-muted-foreground mt-1">
              Enter your {carrier.name} API portal credentials. These are encrypted and stored securely.
            </p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground">Client ID</label>
                <Input
                  value={form.api_client_id}
                  onChange={e => setForm(f => ({ ...f, api_client_id: e.target.value }))}
                  placeholder="Enter client ID"
                  className="text-[13px]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground">Client Secret</label>
                <Input
                  type="password"
                  value={form.api_client_secret}
                  onChange={e => setForm(f => ({ ...f, api_client_secret: e.target.value }))}
                  placeholder="Enter client secret"
                  className="text-[13px]"
                />
              </div>
            </div>
            {carrier.code === 'HLCU' ? (
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground">User Email <span className="text-red-500">*</span> <span className="text-muted-foreground font-normal">(Hapag-Lloyd customerIdentifier)</span></label>
                <Input
                  value={form.api_user_email}
                  onChange={e => setForm(f => ({ ...f, api_user_email: e.target.value }))}
                  placeholder="Email registered with Hapag-Lloyd Online Business"
                  className="text-[13px]"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground">User Email <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Input
                  value={form.api_user_email}
                  onChange={e => setForm(f => ({ ...f, api_user_email: e.target.value }))}
                  placeholder="e.g. api@company.com"
                  className="text-[13px]"
                />
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2.5">
            <Button type="submit" className="h-9 px-4 text-[13px] rounded-lg">
              Save Credentials
            </Button>
            <Button
              type="button"
              variant="outline"
              className={`h-9 px-4 text-[13px] rounded-lg ${canTest ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
              onClick={canTest ? handleTest : undefined}
              disabled={!canTest || testing}
            >
              <Zap className="h-3.5 w-3.5 mr-1.5" />
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={hasCredential ? handleRemove : undefined}
              disabled={!hasCredential}
              className={`flex items-center gap-1.5 py-2 px-4 rounded-lg text-[13px] font-medium ${
                hasCredential
                  ? 'text-red-600 hover:text-red-800 hover:bg-red-50 cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove Credentials
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

ApiCredentials.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
